import { GROUP_TYPE } from '@/constants/area-constants';
import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";
import { AccessGroupCard } from '../../../components/accessGroup/AccessGroupCard';
import { AccessGroupDetails } from '../../../components/accessGroup/AccessGroupDetails';
import AccessGroupEntryActions from '../../../components/accessGroup/AccessGroupEntryActions';
import AccessGroupExitActions from '../../../components/accessGroup/AccessGroupExitActions';
import AssignedTab from '../../../components/assignWizard/AssignedTab';
import AssignModalTwo from '../../../components/assignWizard/AssignModal';
import ManageButton from '../../../components/assignWizard/ManageButton';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { ACCESS_TYPES } from '../../../constants/access-constants';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { AccessGroup as AccessGroupModel } from '../../../models/AccessGroup';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import EnrolmentColumns from "../../../models/ColumnDefinitions/EnrolmentColumns";
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { Name } from '../../../models/Name';
import CategorySearch from '../../../models/search/CategorySearch';
import CompanySearch from '../../../models/search/CompanySearch';
import DepartmentSearch from '../../../models/search/DepartmentSearch';
import EnrolmentSearch from '../../../models/search/EnrolmentSearch';
import SiteSearch from '../../../models/search/SiteSearch';
import { TabSummary } from '../../../models/TabSummary';
import { hideColumns } from '../../../models/utilities/columnHelper';
import { assignUnassignMasterGroupRelationship, getAccessGroupById, getAccessGroupsNamesWithPagination, storeAccessGroup } from '../../../services/accessGroupService';
import { getCategoryNameWithPagination } from '../../../services/categoryService';
import { getCompanyNamesWithPagination } from '../../../services/companyService';
import { getDepartmentNamesWithPagination } from '../../../services/departmentService';
import { getEnrolmentNamesWithPagination, getEnrolments } from '../../../services/enrolmentService';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';
import { PaginationModel } from '@/models/Pagination';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

interface AccessGroupProps {
    accessType: GROUP_TYPE;
}

const AccessGroup: FC<AccessGroupProps> = ({ accessType }) => {
    const initialItem: AccessGroupModel = {
        id: 0,
        name: '',
        enrolments: 0,
        entryActions: 0,
        site: '',
        siteId: 0,
        exitActions: 0,
        default: undefined,
        startTime: undefined,
        description: '',
        duration: undefined,
        days: '',
        dayList: undefined,
        area: '',
        accessTimePatterns: '',
        suspended: false,
        maxCapacity: 0,
        startDate: undefined,
        endDate: undefined,
        accessAreaId: 0,
        accessTimePatternId: 0,
        allowAccess: undefined,
        arming: undefined,
        disarming: undefined,
        number: undefined,
        report: undefined,
        sGroupNumber: undefined
    }
    const { translate } = useLocale();
    const isAccessGroup = accessType === ACCESS_TYPES.ACCESS_GROUP;
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(isAccessGroup ? TranslationKeys.entryActions : TranslationKeys.actions), badge: 0, showInitial: true, isActive: true },
        ...(isAccessGroup ? [
            { label: translate.byKey(TranslationKeys.exitActions), badge: 0, showInitial: true, isActive: true },
        ] : []),
        { label: translate.byKey(TranslationKeys.members), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<AccessGroupModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [showManagedModal, setShowManagedModal] = useState(false);
    const lookups: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];
    const allItemsPath = '/access-control/enrolment/access-groups';
    const itemPath = accessType === ACCESS_TYPES.ACCESS_GROUP ? '/access-control/enrolment/access-group/' : '/access-control/enrolment/floor-group/';

    const getAssignedMembers = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>) => {
        try {
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = [];
            do {
                const enrolments = (await getEnrolments(translate,{ filters: itemId ? appendFilter(itemId, "Access Group", "masterGroups.accessGroup.id", []) : [], pageSize: 100, goToPage: pageNumber }));
                ids = ids.concat(enrolments.rows.map(enrolment => enrolment.id.toString()))
                rows = rows.concat(enrolments.rows);
                pagination = enrolments.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    /**
    * Fetch all topics for the topic list component
    * @returns
    */
    const topic: Topic = {
        accessor: 'agName',
        allItemsPath,
        itemPath,
        title: accessType === ACCESS_TYPES.ACCESS_GROUP ? translate.byKey(TranslationKeys.accessGroups) : translate.byKey(TranslationKeys.floorGroups),
    }

    /**
     * Fetch all the required data on a re-render
     */
    const  reloadPage = async () => {
        try {
            let newCurrentAccessGroup: any;
            if (itemId) {
                newCurrentAccessGroup = await getAccessGroupById(itemId, translate);

                setSelectedItem({label: newCurrentAccessGroup.name, value: newCurrentAccessGroup.id.toString(), isDefault: newCurrentAccessGroup.isDefault})
                setCurrentItem(newCurrentAccessGroup);
                // update the tab badge counts

                const membersIndex = isAccessGroup ? 3 : 2;

                let updatedTabs = updateTabBadgeTotal(1, newCurrentAccessGroup?.entryActions, initialTabs);
                if (isAccessGroup) {
                    updatedTabs = updateTabBadgeTotal(2, newCurrentAccessGroup?.exitActions, updatedTabs);
                }
                updatedTabs = updateTabBadgeTotal(membersIndex, newCurrentAccessGroup?.enrolments, updatedTabs);


                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]);
            }
            setBreadcrumb([
                { value: '/access-control/enrolment', displayName: translate.byKey(TranslationKeys.enrolments) },
                { value: allItemsPath, displayName: translate.byKey(accessType === ACCESS_TYPES.ACCESS_GROUP ? TranslationKeys.accessGroups : TranslationKeys.floorGroups) },
                { value: itemPath + `${itemId}`, displayName: `${newCurrentAccessGroup?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

     /**
     * Content and descriptors for each tab
     */
    const tabContentList: ContentWrapper[] = [{
        content: <AccessGroupDetails />
    },
    {
        content: <AccessGroupEntryActions tabs={tabs} setTabs={setTabs} itemId={itemId} siteId={currentItem?.siteId} reloadPage={reloadPage}/>
    }, 
        ...(isAccessGroup ? [{content: <AccessGroupExitActions tabs={tabs} setTabs={setTabs} itemId={itemId} siteId={currentItem?.siteId} reloadPage={reloadPage} />}] : []),
    {
        content: <AssignedTab
            column={hideColumns(EnrolmentColumns, ["companyName", "deptName"])}
            searchColumn={EnrolmentSearch}
            id={itemId}
            dataMethod={(listParameters?: ListQuery) => getEnrolments(translate, { ...listParameters, filters: appendFilter(itemId, "Access Group", "masterGroups.accessGroup.id", listParameters?.filters ?? []) })}
            lookup={lookups}
            title={translate.byKey(TranslationKeys.assignedMembers)}>
            <ManageButton onClick={() => setShowManagedModal(true)} />
        </AssignedTab>
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }];

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveAccessGroup = (values: any) => {
        setBusy(true);
        const accessGroup: AccessGroupModel = {
            ...currentItem,
            name: values.name,
            siteId: itemId ? values.siteId : site.value,
            accessTimePatternId: values.accessTimePatternId,
            accessAreaId: values.accessAreaId,
            id: +itemId,
            endDate: values.endDate,
            maxCapacity: values.maxCapacity,
            startDate: values.startDate,
            suspended: values.suspended,
            site: itemId ? values.site : site.label,
            area: values.area,
            accessTimePatterns: '',
            description: values.description,
            ...(accessType !== null && { agType: accessType })
        };
        const createNewAccessGroup = itemId ? +itemId < 0 : true;

        storeAccessGroup(accessGroup, createNewAccessGroup)
            .then(async (value: any) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.accessGroupSaved));
                setCurrentItem(accessGroup);
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
        return tabContentList[0].content;
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const assignMembers = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);
        assignUnassignMasterGroupRelationship(toBeAssigned, toBeUnassigned, itemId).then(() => {
            reloadPage();
        }).catch((err) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err) });
        }).finally(() => {
            setBusy(false);
        })
    }

    return <>
        <TopicContextProvider 
            dataMethod={(listQuery?: ListQuery) => getAccessGroupsNamesWithPagination(accessType, listQuery)}
                itemId={itemId} 
                topic={topic} 
                selectedItem={selectedItem} >
                <ItemContentTabTwo>
                    <SimpleItemDetails
                        breadcrumb={breadcrumb}
                        busy={busy}
                        getTabContent={getTabContent}
                        itemProfile={currentItem}
                        onBack={() => history.push(allItemsPath)}
                        saveDetails={saveAccessGroup}
                        tabs={tabs}
                        itemPath={itemPath}
                        initialValues={currentItem}>
                        <AccessGroupCard agType={accessType} />
                    </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
        <AssignModalTwo
            tableColumns={hideColumns(EnrolmentColumns, ["totalTags", "category",])}
            assigningTitle={translate.byKey(TranslationKeys.members)}
            lookup={lookups}
            dataMethod={(listParameters?: ListQuery) => getEnrolments(translate, listParameters)}
            isOpen={showManagedModal}
            onClose={() => { setShowManagedModal(false) }}
            searchColumns={EnrolmentSearch}
            getAssignedItems={getAssignedMembers}
            handleSubmit={assignMembers}
            modalTitle={translate.byKey(TranslationKeys.assignMembers)} />
    </>
}

export default AccessGroup;

